'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function HorsepowerCalculator() {
  const t = useTranslations("tools.horsepowerCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Definition Calculator State
  const [defInputs, setDefInputs] = useState({
    force: 10,
    forceUnit: 'newton',
    distance: 3,
    distanceUnit: 'meter',
    time: 2,
    timeUnit: 'second'
  });
  const [defResults, setDefResults] = useState<any>(null);

  // Converter State
  const [convInputs, setConvInputs] = useState({
    amount: 1,
    fromUnit: 'mechanical-hp',
    toUnit: 'watt'
  });
  const [convResults, setConvResults] = useState<any>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const forceConversions: { [key: string]: number } = {
    'newton': 1,
    'kilonewton': 1000,
    'pound-force': 4.44822,
    'kilogram-force': 9.80665
  };

  const distanceConversions: { [key: string]: number } = {
    'meter': 1,
    'kilometer': 1000,
    'mile': 1609.344,
    'yard': 0.9144
  };

  const timeConversions: { [key: string]: number } = {
    'second': 1,
    'minute': 60,
    'hour': 3600,
    'day': 86400
  };

  const powerConversions: { [key: string]: number } = {
    'watt': 1,
    'kilowatt': 1000,
    'mechanical-hp': 745.699872,
    'metric-hp': 735.49875,
    'electrical-hp': 746,
    'boiler-hp': 9809.5,
    'btu-h': 0.29307107
  };

  const handleDefCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const forceInNewtons = defInputs.force * forceConversions[defInputs.forceUnit];
    const distanceInMeters = defInputs.distance * distanceConversions[defInputs.distanceUnit];
    const timeInSeconds = defInputs.time * timeConversions[defInputs.timeUnit];

    const powerInWatts = (forceInNewtons * distanceInMeters) / timeInSeconds;

    const mechanicalHP = powerInWatts / powerConversions['mechanical-hp'];
    const kilowatts = powerInWatts / 1000;
    const metricHP = powerInWatts / powerConversions['metric-hp'];

    setDefResults({
      mechanicalHP: mechanicalHP.toFixed(6),
      watts: powerInWatts.toFixed(2),
      kilowatts: kilowatts.toFixed(4),
      metricHP: metricHP.toFixed(6)
    });
  };

  const handleConvCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const watts = convInputs.amount * powerConversions[convInputs.fromUnit];
    const result = watts / powerConversions[convInputs.toUnit];

    setConvResults({
      value: result.toFixed(8),
      unit: convInputs.toUnit
    });
  };

  const handleDefClear = () => {
    setDefInputs({
      force: 10,
      forceUnit: 'newton',
      distance: 3,
      distanceUnit: 'meter',
      time: 2,
      timeUnit: 'second'
    });
    setDefResults(null);
  };

  const handleConvClear = () => {
    setConvInputs({
      amount: 1,
      fromUnit: 'mechanical-hp',
      toUnit: 'watt'
    });
    setConvResults(null);
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto' },
    {},
    { maxWidth: '900px' },
    { maxWidth: '1000px' }
  );

  const calculatorCardStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      marginBottom: '1.5rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2rem', borderRadius: '1.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: '#2563eb', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.5rem', marginBottom: '1.25rem' },
    { fontSize: '1.5rem', marginBottom: '1.5rem' }
  );

  const cardDescriptionStyle = getResponsiveStyle(
    { color: '#64748b', marginBottom: '1.5rem' },
    { fontSize: '0.875rem' },
    { fontSize: '0.95rem' },
    { fontSize: '0.95rem' }
  );

  const formulaDisplayStyle = getResponsiveStyle(
    {
      background: '#f8fafc',
      borderLeft: '4px solid #2563eb',
      borderRadius: '0.5rem',
      fontFamily: "'Courier New', monospace",
      color: '#334155',
      marginBottom: '1.5rem'
    },
    { padding: '0.75rem', fontSize: '0.875rem' },
    { padding: '1rem', fontSize: '0.95rem' },
    { padding: '1rem', fontSize: '1rem' }
  );

  const formGroupStyle = {
    display: 'flex',
    flexDirection: 'column' as const,
    marginBottom: '1.25rem'
  };

  const labelStyle = getResponsiveStyle(
    { fontWeight: 600, color: '#334155', marginBottom: '0.5rem' },
    { fontSize: '0.875rem' },
    { fontSize: '0.95rem' },
    { fontSize: '0.95rem' }
  );

  const inputWrapperStyle = {
    display: 'flex',
    gap: '0.5rem',
    flexWrap: 'wrap' as const
  };

  const inputStyle = getResponsiveStyle(
    {
      flex: 1,
      minWidth: '120px',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease',
      background: 'white'
    },
    { padding: '0.625rem', fontSize: '1rem' },
    { padding: '0.75rem', fontSize: '1rem' },
    { padding: '0.75rem', fontSize: '1rem' }
  );

  const selectStyle = getResponsiveStyle(
    {
      minWidth: '120px',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease',
      background: 'white'
    },
    { padding: '0.625rem', fontSize: '0.875rem' },
    { padding: '0.75rem', fontSize: '0.95rem' },
    { padding: '0.75rem', fontSize: '1rem' }
  );

  const buttonGroupStyle = getResponsiveStyle(
    { display: 'flex', gap: '1rem', marginTop: '0.5rem' },
    { flexDirection: 'column' as const },
    { flexDirection: 'row' as const },
    { flexDirection: 'row' as const }
  );

  const calculateBtnStyle = getResponsiveStyle(
    {
      flex: 1,
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem', minHeight: '44px' },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' }
  );

  const clearBtnStyle = getResponsiveStyle(
    {
      flex: 1,
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: '#f1f5f9',
      color: '#475569'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem', minHeight: '44px' },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      marginTop: '2rem',
      background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
      borderRadius: '0.75rem',
      border: '2px solid #86efac'
    },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '1.5rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    { color: '#15803d', marginBottom: '1rem', fontWeight: 600 },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.25rem' }
  );

  const resultItemStyle = getResponsiveStyle(
    {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '0.75rem',
      background: 'white',
      borderRadius: '0.5rem',
      marginBottom: '0.75rem'
    },
    { flexDirection: 'column' as const, gap: '0.5rem', textAlign: 'center' as const },
    { flexDirection: 'row' as const },
    { flexDirection: 'row' as const }
  );

  const resultLabelStyle = getResponsiveStyle(
    { fontWeight: 600, color: '#475569' },
    { fontSize: '0.875rem' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' }
  );

  const resultValueStyle = getResponsiveStyle(
    { fontWeight: 700, color: '#15803d' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.25rem' }
  );

  const conversionGridStyle = getResponsiveStyle(
    {
      display: 'grid',
      gap: '0.75rem',
      marginTop: '1rem'
    },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(3, 1fr)' }
  );

  const conversionItemStyle = {
    padding: '0.75rem',
    background: 'white',
    borderRadius: '0.5rem',
    textAlign: 'center' as const
  };

  const conversionLabelStyle = {
    fontSize: '0.85rem',
    color: '#64748b',
    marginBottom: '0.25rem'
  };

  const conversionValueStyle = getResponsiveStyle(
    { fontWeight: 700, color: '#0f766e' },
    { fontSize: '1rem' },
    { fontSize: '1.1rem' },
    { fontSize: '1.1rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const h3Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem', marginTop: '1.5rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.7, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' }
  );

  const listStyle = getResponsiveStyle(
    { lineHeight: 1.8, color: colors.textSecondary, paddingLeft: '1.5rem', marginBottom: '1rem' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' }
  );

  const faqItemStyle = {
    marginBottom: '1.5rem'
  };

  const faqQuestionStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const faqAnswerStyle = getResponsiveStyle(
    { lineHeight: 1.7, color: colors.textSecondary },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' },
    { fontSize: '1rem' }
  );

  const referenceStyle = {
    marginBottom: '0.75rem'
  };

  const referenceLinkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    fontSize: '0.95rem',
    wordBreak: 'break-word' as const
  };

  const formRowStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr' },
    { gridTemplateColumns: '1fr 1fr' }
  );

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t("title")}</h1>
        <p style={summaryTextStyle}>{t("subtitle")}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={calculatorCardStyle}>
            <h2 style={h2Style}>⚡ {t("defCalculatorTitle")}</h2>
            <p style={cardDescriptionStyle}>{t("defCalculatorDescription")}</p>
            <div style={formulaDisplayStyle}>
              {t("defFormula")}
            </div>

            <form onSubmit={handleDefCalculate}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t("forceLabel")}</label>
                <div style={inputWrapperStyle}>
                  <input
                    type="number"
                    step="any"
                    value={defInputs.force}
                    onChange={(e) => setDefInputs({...defInputs, force: parseFloat(e.target.value) || 0})}
                    style={inputStyle}
                    required
                  />
                  <select
                    value={defInputs.forceUnit}
                    onChange={(e) => setDefInputs({...defInputs, forceUnit: e.target.value})}
                    style={selectStyle}
                  >
                    <option value="newton">{t("newton")}</option>
                    <option value="kilonewton">{t("kilonewton")}</option>
                    <option value="pound-force">{t("poundForce")}</option>
                    <option value="kilogram-force">{t("kilogramForce")}</option>
                  </select>
                </div>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>{t("distanceLabel")}</label>
                <div style={inputWrapperStyle}>
                  <input
                    type="number"
                    step="any"
                    value={defInputs.distance}
                    onChange={(e) => setDefInputs({...defInputs, distance: parseFloat(e.target.value) || 0})}
                    style={inputStyle}
                    required
                  />
                  <select
                    value={defInputs.distanceUnit}
                    onChange={(e) => setDefInputs({...defInputs, distanceUnit: e.target.value})}
                    style={selectStyle}
                  >
                    <option value="meter">{t("meter")}</option>
                    <option value="kilometer">{t("kilometer")}</option>
                    <option value="mile">{t("mile")}</option>
                    <option value="yard">{t("yard")}</option>
                  </select>
                </div>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>{t("timeLabel")}</label>
                <div style={inputWrapperStyle}>
                  <input
                    type="number"
                    step="any"
                    value={defInputs.time}
                    onChange={(e) => setDefInputs({...defInputs, time: parseFloat(e.target.value) || 0})}
                    style={inputStyle}
                    required
                  />
                  <select
                    value={defInputs.timeUnit}
                    onChange={(e) => setDefInputs({...defInputs, timeUnit: e.target.value})}
                    style={selectStyle}
                  >
                    <option value="second">{t("second")}</option>
                    <option value="minute">{t("minute")}</option>
                    <option value="hour">{t("hour")}</option>
                    <option value="day">{t("day")}</option>
                  </select>
                </div>
              </div>

              <div style={buttonGroupStyle}>
                <button type="submit" style={calculateBtnStyle}>{t("calculateButton")}</button>
                <button type="button" onClick={handleDefClear} style={clearBtnStyle}>{t("clearButton")}</button>
              </div>
            </form>

            {defResults && (
              <div style={resultsStyle}>
                <h3 style={resultsH3Style}>📊 {t("resultsTitle")}</h3>
                <div>
                  <div style={resultItemStyle}>
                    <span style={resultLabelStyle}>{t("mechanicalHorsepower")}</span>
                    <span style={resultValueStyle}>{defResults.mechanicalHP} hp</span>
                  </div>
                </div>
                <div style={conversionGridStyle}>
                  <div style={conversionItemStyle}>
                    <div style={conversionLabelStyle}>{t("watts")}</div>
                    <div style={conversionValueStyle}>{defResults.watts} W</div>
                  </div>
                  <div style={conversionItemStyle}>
                    <div style={conversionLabelStyle}>{t("kilowatts")}</div>
                    <div style={conversionValueStyle}>{defResults.kilowatts} kW</div>
                  </div>
                  <div style={conversionItemStyle}>
                    <div style={conversionLabelStyle}>{t("metricHP")}</div>
                    <div style={conversionValueStyle}>{defResults.metricHP} hp(M)</div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div style={calculatorCardStyle}>
            <h2 style={h2Style}>🔄 {t("converterTitle")}</h2>
            <p style={cardDescriptionStyle}>{t("converterDescription")}</p>

            <form onSubmit={handleConvCalculate}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t("amountLabel")}</label>
                <input
                  type="number"
                  step="any"
                  value={convInputs.amount}
                  onChange={(e) => setConvInputs({...convInputs, amount: parseFloat(e.target.value) || 0})}
                  style={inputStyle}
                  required
                />
              </div>

              <div style={formRowStyle}>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("convertFromLabel")}</label>
                  <select
                    value={convInputs.fromUnit}
                    onChange={(e) => setConvInputs({...convInputs, fromUnit: e.target.value})}
                    style={selectStyle}
                  >
                    <option value="watt">{t("watt")}</option>
                    <option value="kilowatt">{t("kilowatt")}</option>
                    <option value="mechanical-hp">{t("mechanicalHorsepowerUnit")}</option>
                    <option value="metric-hp">{t("metricHorsepowerUnit")}</option>
                    <option value="electrical-hp">{t("electricalHorsepower")}</option>
                    <option value="boiler-hp">{t("boilerHorsepower")}</option>
                    <option value="btu-h">{t("btuPerHour")}</option>
                  </select>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("convertToLabel")}</label>
                  <select
                    value={convInputs.toUnit}
                    onChange={(e) => setConvInputs({...convInputs, toUnit: e.target.value})}
                    style={selectStyle}
                  >
                    <option value="watt">{t("watt")}</option>
                    <option value="kilowatt">{t("kilowatt")}</option>
                    <option value="mechanical-hp">{t("mechanicalHorsepowerUnit")}</option>
                    <option value="metric-hp">{t("metricHorsepowerUnit")}</option>
                    <option value="electrical-hp">{t("electricalHorsepower")}</option>
                    <option value="boiler-hp">{t("boilerHorsepower")}</option>
                    <option value="btu-h">{t("btuPerHour")}</option>
                  </select>
                </div>
              </div>

              <div style={buttonGroupStyle}>
                <button type="submit" style={calculateBtnStyle}>{t("convertButton")}</button>
                <button type="button" onClick={handleConvClear} style={clearBtnStyle}>{t("clearButton")}</button>
              </div>
            </form>

            {convResults && (
              <div style={resultsStyle}>
                <h3 style={resultsH3Style}>📊 {t("conversionResultTitle")}</h3>
                <div>
                  <div style={resultItemStyle}>
                    <span style={resultLabelStyle}>{t("resultLabel")}</span>
                    <span style={resultValueStyle}>{convResults.value}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("introTitle")}</h2>
        <p style={paragraphStyle}>{t("introParagraph1")}</p>
        <p style={paragraphStyle}>{t("introParagraph2")}</p>
        <p style={paragraphStyle}>{t("introParagraph3")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("howToUseTitle")}</h2>
        <h3 style={h3Style}>{t("defCalculatorSubtitle")}</h3>
        <ol style={listStyle}>
          <li>{t("howToUseStep1")}</li>
          <li>{t("howToUseStep2")}</li>
          <li>{t("howToUseStep3")}</li>
          <li>{t("howToUseStep4")}</li>
          <li>{t("howToUseStep5")}</li>
        </ol>

        <h3 style={h3Style}>{t("converterSubtitle")}</h3>
        <ol style={listStyle}>
          <li>{t("converterStep1")}</li>
          <li>{t("converterStep2")}</li>
          <li>{t("converterStep3")}</li>
          <li>{t("converterStep4")}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("insightsTitle")}</h2>
        <p style={paragraphStyle}>{t("insight1")}</p>
        <p style={paragraphStyle}>{t("insight2")}</p>
        <p style={paragraphStyle}>{t("insight3")}</p>
        <p style={paragraphStyle}>{t("insight4")}</p>
        <p style={paragraphStyle}>{t("insight5")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("detailsTitle")}</h2>

        <h3 style={h3Style}>{t("whatIsHorsepowerTitle")}</h3>
        <p style={paragraphStyle}>{t("whatIsHorsepowerText")}</p>

        <h3 style={h3Style}>{t("typesOfHorsepowerTitle")}</h3>
        <ul style={listStyle}>
          <li><strong>{t("mechanicalHorsepowerLabel")}</strong> {t("mechanicalHorsepowerDesc")}</li>
          <li><strong>{t("metricHorsepowerLabel")}</strong> {t("metricHorsepowerDesc")}</li>
          <li><strong>{t("electricalHorsepowerLabel")}</strong> {t("electricalHorsepowerDesc")}</li>
          <li><strong>{t("boilerHorsepowerLabel")}</strong> {t("boilerHorsepowerDesc")}</li>
        </ul>

        <h3 style={h3Style}>{t("calculationFormulasTitle")}</h3>
        <p style={paragraphStyle}>{t("formulaIntro")}</p>
        <ul style={listStyle}>
          <li><strong>{t("basicFormulaLabel")}</strong> {t("basicFormulaText")}</li>
          <li><strong>{t("torqueFormulaLabel")}</strong> {t("torqueFormulaText")}</li>
          <li><strong>{t("conversionFormulaLabel")}</strong> {t("conversionFormulaText")}</li>
        </ul>

        <h3 style={h3Style}>{t("applicationsTitle")}</h3>
        <ul style={listStyle}>
          <li>{t("application1")}</li>
          <li>{t("application2")}</li>
          <li>{t("application3")}</li>
          <li>{t("application4")}</li>
          <li>{t("application5")}</li>
        </ul>

        <h3 style={h3Style}>{t("bestPracticesTitle")}</h3>
        <ul style={listStyle}>
          <li>{t("bestPractice1")}</li>
          <li>{t("bestPractice2")}</li>
          <li>{t("bestPractice3")}</li>
          <li>{t("bestPractice4")}</li>
          <li>{t("bestPractice5")}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("faqTitle")}</h2>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq1Question")}</h3>
          <p style={faqAnswerStyle}>{t("faq1Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq2Question")}</h3>
          <p style={faqAnswerStyle}>{t("faq2Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq3Question")}</h3>
          <p style={faqAnswerStyle}>{t("faq3Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq4Question")}</h3>
          <p style={faqAnswerStyle}>{t("faq4Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq5Question")}</h3>
          <p style={faqAnswerStyle}>{t("faq5Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq6Question")}</h3>
          <p style={faqAnswerStyle}>{t("faq6Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq7Question")}</h3>
          <p style={faqAnswerStyle}>{t("faq7Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq8Question")}</h3>
          <p style={faqAnswerStyle}>{t("faq8Answer")}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("referencesTitle")}</h2>
        <div style={referenceStyle}>
          <a href="https://mypocketcalculator.com/hp-tq-calculator/" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
            HP TQ Calculator: Powerful and Easy-to-Use Tool
          </a>
        </div>
        <div style={referenceStyle}>
          <a href="https://www.techtarget.com/whatis/definition/horsepower-hp" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
            What is horsepower (hp) and how is it measured? - TechTarget
          </a>
        </div>
        <div style={referenceStyle}>
          <a href="https://www.groschopp.com/what-is-horsepower-hp/" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
            What is horsepower (hp)? - Groschopp
          </a>
        </div>
        <div style={referenceStyle}>
          <a href="https://en.wikipedia.org/wiki/Horsepower" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
            Horsepower - Wikipedia
          </a>
        </div>
        <div style={referenceStyle}>
          <a href="https://www.calculator.net/horsepower-calculator.html" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
            Horsepower Calculator - Calculator.net
          </a>
        </div>
        <div style={referenceStyle}>
          <a href="https://spicerparts.com/calculators/horsepower-torque-calculator" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
            Horsepower and Torque Calculator - Spicer Parts
          </a>
        </div>
        <div style={referenceStyle}>
          <a href="https://joycedayton.com/technical-help/horsepower-calculation" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
            Horsepower Calculation - Joyce Dayton
          </a>
        </div>
        <div style={referenceStyle}>
          <a href="https://www.aberdeendynamics.com/how-to-use-a-hydraulic-horsepower-calculator" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
            How To Use a Hydraulic Horsepower Calculator - Aberdeen Dynamics
          </a>
        </div>
        <div style={referenceStyle}>
          <a href="https://pubs.nmsu.edu/_m/M227/index.html" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
            Understanding Horsepower and Water Horsepower - NMSU Publications
          </a>
        </div>
      </section>
    </div>
  );
}